import * as React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
    Grid,
    Paper,
    Link as MuiLink,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    OpenInNew as OpenInNewIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    Category as CategoryIcon,
    CalendarToday as CalendarIcon,
    Description as DescriptionIcon,
    Score as ScoreIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export default function LicitacaoDetailDialog({ open, onClose, licitacao }) {
    if (!licitacao) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'Não informada';
        return dateString;
    };

    const getScoreStatus = (score) => {
        if (score.matched) {
            return { color: 'success', icon: <CheckCircleIcon />, text: 'Aprovado' };
        } else if (score.inclusaoMatches > 0 && score.exclusaoMatches === 0) {
            return { color: 'warning', icon: <CheckCircleIcon />, text: 'Pre Aprovado' };
        }
        return { color: 'default', icon: <CancelIcon />, text: 'Não aprovado' };
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" component="div">
                        Detalhes da Licitação
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Cabeçalho com número e tipo */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Número
                                    </Typography>
                                    <Typography variant="h6">
                                        {licitacao.numero || 'Não informado'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Tipo
                                    </Typography>
                                    <Typography variant="body1">
                                        {licitacao.tipo || 'Não informado'}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={licitacao.fonte || 'Fonte não informada'}
                                    color="primary"
                                    size="small"
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Órgão e Unidade */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Órgão
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            {licitacao.orgaos || 'Não informado'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <BusinessIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Unidade
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            {licitacao.unidade || 'Não informado'}
                        </Typography>
                    </Grid>

                    {/* Localização */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Localização
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            {licitacao.cidadeUf || 'Não informado'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            UF: {licitacao.uf || 'Não informado'}
                        </Typography>
                    </Grid>

                    {/* Datas */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Datas
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2">
                                <strong>Abertura:</strong> {formatDate(licitacao.dataAbertura)} às {licitacao.horaAbertura || '-'}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Busca:</strong> {licitacao.dataBusca} ({licitacao.periodoBusca})
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Grupos */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <CategoryIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Classificação
                            </Typography>
                        </Box>
                        <Typography variant="body2">
                            <strong>Grupo:</strong> {licitacao.grupo || 'Não informado'}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Subgrupo:</strong> {licitacao.subGrupo || 'Não informado'}
                        </Typography>
                    </Grid>

                    {/* Edital */}
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <DescriptionIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Documentação
                            </Typography>
                        </Box>
                        {licitacao.edital ? (
                            <MuiLink
                                href={licitacao.edital}
                                target="_blank"
                                rel="noopener noreferrer"
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                            >
                                <OpenInNewIcon fontSize="small" />
                                Acessar Edital
                            </MuiLink>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                Edital não disponível
                            </Typography>
                        )}
                    </Grid>

                    {/* Objeto */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <DescriptionIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Objeto
                            </Typography>
                        </Box>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {licitacao.objeto || 'Não informado'}
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Scores/Filtros */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <ScoreIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" color="primary">
                                Resultado dos Filtros
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            {licitacao.scores?.map((score, index) => {
                                const status = getScoreStatus(score);
                                const temPalavras = (score.palavrasInclusaoEncontradas?.length > 0 ||
                                    score.palavrasExclusaoEncontradas?.length > 0);

                                return (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                borderColor: score.matched ? 'success.main' : 'grey.300',
                                                bgcolor: score.matched ? '#4caf4fd8' : 'background.paper'
                                            }}
                                        >
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="subtitle2">
                                                    {score.nome}
                                                </Typography>
                                                <Chip
                                                    label={status.text}
                                                    color={status.color}
                                                    size="small"
                                                    icon={status.icon}
                                                />
                                            </Box>

                                            <Typography variant="caption" color="textSecondary">
                                                Tipo: {score.tipo} | Prioridade: {score.prioridade}
                                            </Typography>

                                            <Box mt={1}>
                                                <Typography variant="caption">
                                                    Matches: {score.totalMatches}
                                                    ({score.inclusaoMatches} inclusão / {score.exclusaoMatches} exclusão)
                                                </Typography>
                                            </Box>

                                            {temPalavras && (
                                                <Accordion
                                                    sx={{
                                                        mt: 1,
                                                        bgcolor: 'transparent',
                                                        boxShadow: 'none',
                                                        '&:before': { display: 'none' }
                                                    }}
                                                    elevation={0}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            p: 0,
                                                            minHeight: 'auto',
                                                            '& .MuiAccordionSummary-content': { m: 0 }
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="primary">
                                                            Ver palavras encontradas
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ p: 0, pt: 1 }}>
                                                        {score.palavrasInclusaoEncontradas?.length > 0 && (
                                                            <Box mb={1}>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Inclusão:
                                                                </Typography>
                                                                <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                                                    {score.palavrasInclusaoEncontradas.map((palavra, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={<span style={{ whiteSpace: 'pre-wrap' }}>{palavra}</span>}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {score.palavrasExclusaoEncontradas?.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    Exclusão:
                                                                </Typography>
                                                                <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                                                    {score.palavrasExclusaoEncontradas.map((palavra, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={<span style={{ whiteSpace: 'pre-wrap' }}>{palavra}</span>}
                                                                            size="small"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            )}
                                        </Paper>
                                    </Grid>
                                );
                            })}
                            {(!licitacao.scores || licitacao.scores.length === 0) && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        Nenhum filtro aplicado
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    {/* Metadados */}
                    <Grid item xs={12}>
                        <Divider />
                        <Box mt={2}>
                            <Typography variant="caption" color="textSecondary">
                                <strong>ID:</strong> {licitacao.id}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                                <strong>Cache Key:</strong> {licitacao.cacheKey}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                                <strong>Criado em:</strong> {new Date(licitacao.dataCriacao).toLocaleString()}
                            </Typography>
                            {licitacao.dataAtualizacao && (
                                <>
                                    <br />
                                    <Typography variant="caption" color="textSecondary">
                                        <strong>Atualizado em:</strong> {new Date(licitacao.dataAtualizacao).toLocaleString()}
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Fechar
                </Button>
                {licitacao.edital && (
                    <Button
                        component="a"
                        href={licitacao.edital}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        color="primary"
                        startIcon={<OpenInNewIcon />}
                    >
                        Abrir Edital
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}